import React, { memo, useEffect, useRef } from 'react';
import moment from 'moment';
import TopBar, { CALENDAR_VIEW_KEYS } from './TopBar';
import { buildFrequentPlaceInputs, computeFrequentPlaces } from './frequentPlaces';
import { getRevisitSuggestions, REVISIT_THRESHOLD_DAYS } from './revisitSuggestions';
import {
  MOVES_ROUTE_TYPES,
  ROUTE_EVENT_TYPES,
  isRouteEventType as registryIsRouteEventType,
} from './features/registry';
import {
  NORMAL_ROUTE_STYLES,
  getRoutePoints,
  splitRoutePointsByDistance,
  MOVES_MAX_POINT_GAP_KM_BY_TYPE,
} from './MapRuntime';
import AvButton from './design/nedi/AvButton';
import { t } from './i18n';
import './design/atlasVivo/Buttons.css';

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const formatRouteDate = (date) => date ? moment(date).format('DD/MM/YYYY') : t('label.noDate');

const makeRouteSearchText = (parts) => normalizeSearchText(parts.filter(Boolean).join(' '));

export const isRouteEventType = registryIsRouteEventType;

export const formatSearchDate = (value) => {
  if (!value) return { isoDate: '', dateLabel: '' };

  const raw = String(value);
  const parsed = moment(raw);
  if (!parsed.isValid()) return { isoDate: raw, dateLabel: raw };

  return {
    isoDate: parsed.format('YYYY-MM-DD'),
    dateLabel: parsed.format('DD/MM/YYYY'),
  };
};

const compactText = (...parts) => parts.filter(Boolean).join(' · ');

export const GLOBAL_SEARCH_SOURCE_MAP = Object.freeze({
  checkin: {
    dataKey: 'checkins',
    sourceLabel: 'Foursquare',
    type: 'checkin',
    actionType: 'flyToMarker',
    title: item => item.venue || item.name || item.place_name || t('web.source.checkinFallback'),
    subtitle: item => compactText(item.city, item.address, t('web.source.checkinFallback')),
    date: item => item.date || item.created_at,
    terms: item => [item.venue, item.name, item.city, item.address, item.category, 'checkin', 'foursquare'],
  },
  movie: {
    dataKey: 'movies',
    sourceLabel: 'Trakt',
    type: 'movie',
    actionType: 'selectCalendarDate',
    title: item => item.title && item.year ? `${item.title} (${item.year})` : item.title,
    subtitle: () => t('web.source.movieTrakt'),
    date: item => item.watched_at,
    terms: item => [item.title, item.year, item.item_type, 'filme', 'movie', 'trakt'],
  },
  shows: {
    dataKey: 'shows',
    sourceLabel: 'Trakt',
    type: 'shows',
    actionType: 'selectCalendarDate',
    title: item => item.show_title || item.title || item.episode_title,
    subtitle: item => compactText(
      item.season_number ? `T${String(item.season_number).padStart(2, '0')}` : '',
      item.episode_number ? `E${String(item.episode_number).padStart(2, '0')}` : '',
      item.episode_title,
      t('web.source.traktSeries')
    ),
    date: item => item.watched_at,
    terms: item => [
      item.show_title,
      item.title,
      item.episode_title,
      item.season_number,
      item.episode_number,
      'serie',
      'série',
      'shows',
      'trakt',
    ],
  },
  concerts: {
    dataKey: 'concerts',
    sourceLabel: 'setlist.fm',
    type: 'concerts',
    actionType: 'openCard',
    title: item => item.artist_name || item.artist || t('web.source.showFallback'),
    subtitle: item => compactText(item.venue_name, item.city_name, item.country_name, 'setlist.fm'),
    date: item => item.event_date,
    terms: item => [
      item.artist_name,
      item.artist,
      item.venue_name,
      item.city_name,
      item.country_name,
      'show',
      'concert',
      'setlistfm',
      'setlist.fm',
    ],
  },
  diary: {
    dataKey: 'diary',
    sourceLabelKey: 'label.diary',
    type: 'diary',
    actionType: 'openCard',
    title: item => item.note_title || item.title || t('label.diary'),
    subtitle: item => compactText(item.mood, item.activities, item.note || item.text),
    date: item => item.entry_date || item.date,
    terms: item => [item.note_title, item.title, item.mood, item.activities, item.note, item.text, 'diario', 'diário'],
  },
  spotify: {
    dataKey: 'spotify',
    sourceLabel: 'Spotify',
    type: 'spotify',
    actionType: 'openCard',
    title: item => item.track_name || item.trackName || t('label.music'),
    subtitle: item => compactText(item.artist_name || item.artistName, item.album_name || item.albumName, 'Spotify'),
    date: item => item.played_at || item.ts || item.endTime,
    terms: item => [
      item.track_name,
      item.trackName,
      item.artist_name,
      item.artistName,
      item.album_name,
      item.albumName,
      'spotify',
      'musica',
      'música',
    ],
  },
  apple_music: {
    dataKey: 'appleMusic',
    sourceLabel: 'Apple Music',
    type: 'apple_music',
    actionType: 'openCard',
    title: item => item.track_name || item.content_name || item['Content Name'] || t('label.music'),
    subtitle: item => compactText(
      item.artist_name || item['Artist Name'],
      item.album_name || item['Album Name'],
      'Apple Music'
    ),
    date: item => item.played_at || item.event_start_timestamp || item['Event Start Timestamp'],
    terms: item => [
      item.track_name,
      item.content_name,
      item['Content Name'],
      item.artist_name,
      item['Artist Name'],
      item.album_name,
      item['Album Name'],
      'apple music',
      'musica',
      'música',
    ],
  },
  github: {
    dataKey: 'github',
    sourceLabel: 'GitHub',
    type: 'github',
    actionType: 'openGithub',
    title: item => item.repository || item.repo || item.repo_name || 'GitHub',
    subtitle: item => compactText(
      item.count !== undefined ? t('web.github.contributionsCount', { count: item.count }) : '',
      item.commit_count !== undefined ? t('web.github.commitsCount', { count: item.commit_count }) : '',
      'GitHub'
    ),
    date: item => item.date || item.day,
    terms: item => [item.repository, item.repo, item.repo_name, item.count, item.commit_count, 'github', 'codigo', 'código'],
  },
});

export const normalizeCalendarView = (view) => (
  CALENDAR_VIEW_KEYS.includes(view) ? view : 'month'
);

export const getAppTarget = () => {
  const envTarget = import.meta.env.VITE_TARGET;
  if (['web', 'desktop', 'mobile'].includes(envTarget)) return envTarget;
  if (typeof window !== 'undefined' && window.innerWidth <= 768) return 'mobile';
  return 'web';
};

export const buildRouteSearchItems = (data = {}, selectedTypes = []) => {
  const selected = new Set(selectedTypes || []);
  const shouldShowHealth = selected.size === 0 || selected.has('health');
  const shouldShowMoves = selected.size === 0 || MOVES_ROUTE_TYPES.some(t => selected.has(t));
  const shouldShowGoogleMaps = selected.size === 0 || selected.has('google_maps');
  const items = [];

  if (shouldShowHealth) {
    (data.health || []).forEach((route, index) => {
      const points = getRoutePoints(route);
      if (points.length < 2) return;

      const firstPoint = route[0] || {};
      const routeGroup = firstPoint.route_group;
      const routeDate = firstPoint.route_date || '';
      const dateLabel = formatRouteDate(routeDate);
      const durationLabel = firstPoint.duration ? `${Math.round(firstPoint.duration / 60)} min` : '';
      const distanceLabel = firstPoint.distance ? `${(firstPoint.distance / 1000).toFixed(2)} km` : '';
      const label = t('web.health.walkLabel', { n: index + 1 });
      const popupLines = [
        label,
        t('web.label.date', { date: dateLabel }),
        durationLabel && t('web.health.duration', { value: durationLabel }),
        distanceLabel && t('web.health.distance', { value: distanceLabel }),
      ].filter(Boolean);

      items.push({
        id: routeGroup ? `health-group-${routeGroup}` : `health-${index}-${routeDate || 'sem-data'}`,
        source: t('label.health'),
        sourceKey: 'health',
        eventType: 'health',
        label,
        routeType: 'workout',
        typeLabel: 'workout',
        date: routeDate,
        dateLabel,
        summary: [durationLabel, distanceLabel].filter(Boolean).join(' · '),
        points,
        routeGroup,
        qualityRouteKey: routeGroup ? `health:${routeGroup}` : null,
        route,
        color: NORMAL_ROUTE_STYLES.health.color,
        style: NORMAL_ROUTE_STYLES.health,
        popupHtml: popupLines.join('<br>'),
        searchText: makeRouteSearchText([
          label,
          'Saúde',
          'Saude',
          'Health',
          'workout',
          routeDate,
          dateLabel,
          durationLabel,
          distanceLabel,
        ]),
      });
    });
  }

  if (shouldShowMoves) {
    (data.moves || []).forEach((route, index) => {
      const firstPoint = Array.isArray(route) ? route[0] || {} : {};
      const routeType = firstPoint.route_type || 'walking';
      const routeGroup = firstPoint.route_group;
      
      // Filter out if this specific moves route type is not selected
      if (selected.size > 0 && !selected.has(routeType)) return;

      const points = getRoutePoints(route);
      if (points.length < 2) return;
      const maxPointGapKm = MOVES_MAX_POINT_GAP_KM_BY_TYPE[routeType];
      const lineSegments = maxPointGapKm === undefined
        ? undefined
        : splitRoutePointsByDistance(points, maxPointGapKm);

      const routeDate = firstPoint.route_date || '';
      const dateLabel = formatRouteDate(routeDate);
      const label = `Rota Moves #${index + 1} (${routeType})`;
      const styleKey = ROUTE_EVENT_TYPES.includes(routeType) ? routeType : 'walking';

      items.push({
        id: routeGroup ? `moves-group-${routeGroup}` : `moves-${index}-${routeDate || 'sem-data'}-${routeType}`,
        source: 'Moves',
        sourceKey: 'moves',
        eventType: routeType,
        label,
        routeType,
        typeLabel: routeType,
        date: routeDate,
        dateLabel,
        summary: routeType,
        points,
        routeGroup,
        qualityRouteKey: routeGroup ? `moves:${routeGroup}` : null,
        ...(lineSegments !== undefined ? { lineSegments } : {}),
        route,
        color: NORMAL_ROUTE_STYLES[styleKey].color,
        style: NORMAL_ROUTE_STYLES[styleKey],
        popupHtml: `${label}<br>Data: ${dateLabel}<br>Tipo: ${routeType}`,
        searchText: makeRouteSearchText([
          label,
          'Moves',
          routeType,
          routeDate,
          dateLabel,
        ]),
      });
    });
  }

  if (shouldShowGoogleMaps) {
    (data.googleMaps || []).forEach((route, index) => {
      const points = getRoutePoints(route);
      if (points.length < 2) return;
      const lineSegments = splitRoutePointsByDistance(points);

      const firstPoint = Array.isArray(route) ? route[0] || {} : {};
      const routeDate = firstPoint.route_date || '';
      const dateLabel = formatRouteDate(routeDate);
      const routeType = firstPoint.route_type || 'timeline';
      const sourceFormat = firstPoint.source_format || 'takeout';
      const routeGroup = firstPoint.route_group;
      const label = `Google Maps #${index + 1}`;

      items.push({
        id: routeGroup ? `google-maps-group-${routeGroup}` : `google-maps-${index}-${routeDate || 'sem-data'}-${routeType}`,
        source: 'Google Maps',
        sourceKey: 'google_maps',
        eventType: 'google_maps',
        label,
        routeType,
        typeLabel: routeType,
        date: routeDate,
        dateLabel,
        summary: sourceFormat,
        points,
        routeGroup,
        qualityRouteKey: routeGroup ? `googleMaps:${routeGroup}` : null,
        lineSegments,
        route,
        color: NORMAL_ROUTE_STYLES.google_maps.color,
        style: NORMAL_ROUTE_STYLES.google_maps,
        popupHtml: `${label}<br>Data: ${dateLabel}<br>Tipo: ${routeType}<br>Origem: ${sourceFormat}`,
        searchText: makeRouteSearchText([
          label,
          'Google Maps',
          'Google Timeline',
          routeType,
          sourceFormat,
          routeDate,
          dateLabel,
        ]),
      });
    });
  }

  return items;
};

export const filterRouteSearchItems = (routeItems, searchTerm) => {
  const query = normalizeSearchText(searchTerm);
  if (!query) return routeItems;

  return routeItems.filter(item => item.searchText.includes(query));
};

export const buildGlobalSearchItems = (
  data = {},
  routeItems = [],
  sourceMap = GLOBAL_SEARCH_SOURCE_MAP
) => {
  const items = [];

  (routeItems || []).forEach(routeItem => {
    if (!routeItem?.id || !routeItem?.label) return;
    items.push({
      id: `route-${routeItem.id}`,
      type: 'route',
      sourceKey: routeItem.sourceKey,
      dataKey: routeItem.sourceKey,
      title: routeItem.label,
      subtitle: compactText(routeItem.source, routeItem.typeLabel, routeItem.summary),
      date: routeItem.date || '',
      dateLabel: routeItem.dateLabel || formatSearchDate(routeItem.date).dateLabel,
      action: { type: 'focusRoute', routeId: routeItem.id },
      item: routeItem,
      searchText: makeRouteSearchText([
        routeItem.label,
        routeItem.source,
        routeItem.sourceKey,
        routeItem.eventType,
        routeItem.typeLabel,
        routeItem.summary,
        routeItem.date,
        routeItem.dateLabel,
      ]),
    });
  });

  Object.entries(sourceMap).forEach(([sourceKey, config]) => {
    const rows = data[config.dataKey] || [];
    if (!Array.isArray(rows)) return;

    rows.forEach((item, index) => {
      if (!item) return;

      const title = config.title(item);
      const rawDate = config.date(item);
      const { isoDate, dateLabel } = formatSearchDate(rawDate);
      const subtitle = config.subtitle(item);
      const sourceLabel = config.sourceLabelKey ? t(config.sourceLabelKey) : config.sourceLabel;

      if (!title && !isoDate && !subtitle) return;

      items.push({
        id: `${sourceKey}-${item.id || isoDate || index}`,
        type: config.type,
        sourceKey,
        dataKey: config.dataKey,
        title: title || sourceLabel,
        subtitle: subtitle || sourceLabel,
        date: isoDate,
        dateLabel,
        action: {
          type: config.actionType,
          sourceKey,
          dataKey: config.dataKey,
          itemId: item.id,
          date: isoDate,
        },
        item,
        searchText: makeRouteSearchText([
          title,
          subtitle,
          sourceLabel,
          sourceKey,
          config.type,
          isoDate,
          dateLabel,
          ...(config.terms(item) || []),
        ]),
      });
    });
  });

  return items;
};

export const filterGlobalSearchItems = (items, searchTerm) => {
  const query = normalizeSearchText(searchTerm);
  if (!query) return items;

  return (items || []).filter(item => item.searchText?.includes(query));
};

export const getGlobalSearchPreviewItems = (items, searchTerm, limit = 24) => {
  const filtered = filterGlobalSearchItems(items, searchTerm);
  return searchTerm ? filtered.slice(0, limit) : filtered.slice(0, Math.min(8, limit));
};

export const isDateOutsidePeriod = (date, startDate, endDate) => {
  if (!date || !startDate || !endDate) return Boolean(date);
  const day = String(date).slice(0, 10);
  return day < startDate || day > endDate;
};

export const expandPeriodToIncludeDate = (startDate, endDate, date) => {
  const day = date ? String(date).slice(0, 10) : null;
  if (!day) return { startDate, endDate };
  return {
    startDate: !startDate || day < startDate ? day : startDate,
    endDate: !endDate || day > endDate ? day : endDate,
  };
};

export const mapServerSearchHit = (hit, index = 0) => {
  const day = hit?.date ? String(hit.date).slice(0, 10) : '';
  const { dateLabel } = formatSearchDate(day);
  return {
    id: `server-${hit?.type || 'hit'}-${hit?.venueId || hit?.label || index}-${day}`,
    type: hit?.type || 'search',
    title: hit?.label || t('web.search.resultFallback'),
    subtitle: t('web.search.outOfPeriod'),
    date: day,
    dateLabel,
    venueId: hit?.venueId || null,
    outOfPeriod: true,
    action: { type: 'jumpToPeriod', date: day, venueId: hit?.venueId || null },
    item: hit,
    searchText: makeRouteSearchText([hit?.label, hit?.type, day]),
  };
};

/** Hits do `/api/search` cuja data cai fora do período atual (seção "Em outros períodos"). */
export const getOutOfPeriodSearchItems = (hits, startDate, endDate, localItems = []) => {
  const localKeys = new Set(
    (localItems || []).map(item => `${normalizeSearchText(item.title || '')}|${item.date || ''}`)
  );

  return (hits || [])
    .filter(hit => {
      const day = hit?.date ? String(hit.date).slice(0, 10) : '';
      if (day && startDate && endDate && !isDateOutsidePeriod(day, startDate, endDate)) {
        return false;
      }
      const key = `${normalizeSearchText(hit?.label || '')}|${day}`;
      return !localKeys.has(key);
    })
    .map((hit, index) => mapServerSearchHit(hit, index));
};

export const buildRevisitSuggestionItems = (currentDate, data = {}) => {
  const visits = computeFrequentPlaces(buildFrequentPlaceInputs(data), { topN: Number.MAX_SAFE_INTEGER });
  const suggestions = getRevisitSuggestions(currentDate, visits, REVISIT_THRESHOLD_DAYS);

  return suggestions.map((suggestion, index) => {
    const { dateLabel } = formatSearchDate(suggestion.lastDate);
    return {
      id: `revisit-${suggestion.nameApprox}-${index}`,
      type: 'suggestion',
      sourceKey: 'revisit',
      dataKey: 'revisit',
      title: t('web.search.revisitTitle', { name: suggestion.nameApprox }),
      subtitle: t('web.search.lastVisit', { date: dateLabel, days: suggestion.daysSinceLastVisit }),
      date: suggestion.lastDate,
      dateLabel,
      action: { type: 'flyToMarker' },
      item: { lat: suggestion.lat, lon: suggestion.lon, venue: suggestion.nameApprox, category: t('web.search.suggestionCategory') },
      searchText: makeRouteSearchText(['volte', 'sugestão', suggestion.nameApprox, dateLabel]),
    };
  });
};

export const buildQualityHiddenRouteKeys = (hiddenIssueIds, issues) => {
  const hiddenSet = new Set(hiddenIssueIds || []);
  const keys = new Set();

  (issues || []).forEach(issue => {
    if (!hiddenSet.has(issue.id)) return;
    if (issue.target?.kind !== 'route') return;
    if (!issue.target?.source || !issue.target?.routeGroup) return;
    keys.add(`${issue.target.source}:${issue.target.routeGroup}`);
  });

  return keys;
};

export const getCalendarDateRange = (date, view) => {
  const baseDate = moment(date || new Date());
  const normalizedView = normalizeCalendarView(view);
  let start;
  let end;

  if (normalizedView === 'week') {
    start = moment(baseDate).startOf('week');
    end = moment(baseDate).endOf('week');
  } else if (normalizedView === 'day') {
    start = moment(baseDate).startOf('day');
    end = moment(baseDate).endOf('day');
  } else if (normalizedView === 'agenda') {
    start = moment(baseDate).startOf('day');
    end = moment(baseDate).add(30, 'days').endOf('day');
  } else {
    start = moment(baseDate).startOf('month');
    end = moment(baseDate).endOf('month');
  }

  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
};

const EMPTY_HIGHLIGHTS = Object.freeze([]);

const TIMELINE_HIGHLIGHT_STYLES = Object.freeze({
  city_debut: { icon: '🗺', color: '#60a5fa' },
  concert_day: { icon: '♪', color: '#2dd4bf' },
  long_workout: { icon: '↗', color: '#4ade80' },
  music_peak: { icon: '♫', color: '#f472b6' },
  github_peak: { icon: '⌂', color: '#f59e0b' },
  checkin_burst: { icon: '◉', color: '#a78bfa' },
  route_burst: { icon: '➝', color: '#fb7185' },
  diary_streak: { icon: '✎', color: '#22d3ee' },
  entertainment_binge: { icon: '▣', color: '#f97316' },
});

export const getTimelineHighlightsForDate = (timelineHighlights, date) => {
  const day = date ? moment(date).format('YYYY-MM-DD') : null;
  if (!day) return EMPTY_HIGHLIGHTS;
  return timelineHighlights?.byDate?.[day] || EMPTY_HIGHLIGHTS;
};

export const TimelineHighlightBadges = memo(function TimelineHighlightBadges({
  highlights = [],
  onSelectDate,
  compact = false,
}) {
  if (!highlights.length) return null;

  return (
    <div className={`timeline-highlight-badges${compact ? ' is-compact' : ''}`}>
      {highlights.map((highlight) => {
        const style = TIMELINE_HIGHLIGHT_STYLES[highlight.key] || { icon: '•', color: '#94a3b8' };
        const itemKey = highlight.id || `${highlight.key}-${highlight.date}`;
        const commonProps = {
          className: 'timeline-highlight-badge',
          style: { '--timeline-highlight-color': style.color },
          title: `${highlight.label}: ${highlight.reason}`,
        };

        const content = (
          <>
            <span className="timeline-highlight-badge-icon" aria-hidden="true">{style.icon}</span>
            <span className="timeline-highlight-badge-label">{highlight.shortLabel}</span>
          </>
        );

        if (onSelectDate) {
          return (
            <button
              key={itemKey}
              {...commonProps}
              type="button"
              aria-label={`Ver destaque ${highlight.label} em ${moment(highlight.date).format('DD/MM/YYYY')}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSelectDate(highlight.date);
              }}
            >
              {content}
            </button>
          );
        }

        return (
          <span key={itemKey} {...commonProps}>
            {content}
          </span>
        );
      })}
    </div>
  );
});

export const TimelineHighlightsSummary = memo(function TimelineHighlightsSummary({ date, highlights = [] }) {
  if (!highlights.length) return null;

  return (
    <section className="timeline-highlights-summary" aria-label={t('web.highlights.title')}>
      <div className="timeline-highlights-summary-header">
        <h4>{t('web.highlights.title')}</h4>
        <span>{date ? moment(date).format('DD/MM/YYYY') : ''}</span>
      </div>
      <TimelineHighlightBadges highlights={highlights} />
      <ul className="timeline-highlights-summary-list">
        {highlights.map((highlight) => (
          <li key={highlight.id || `${highlight.key}-${highlight.date}`}>
            <strong>{highlight.label}</strong>
            <span>{highlight.reason}</span>
          </li>
        ))}
      </ul>
    </section>
  );
});

export const MapLayerLegend = memo(function MapLayerLegend({ items, isolatedLayerKey, onToggle }) {
  if (!items.length) return null;

  return (
    <div className="map-layer-legend" aria-label={t('web.layers.active')}>
      <h3>{t('web.layers.active')}</h3>
      <div className="map-layer-list">
        {items.map(item => {
          const isIsolated = isolatedLayerKey === item.key;
          const isMuted = Boolean(isolatedLayerKey) && !isIsolated;

          return (
            <button
              type="button"
              className={`map-layer-item${isIsolated ? ' is-isolated' : ''}${isMuted ? ' is-muted' : ''}`}
              key={item.key}
              aria-label={isIsolated ? t('web.layers.showAll') : t('web.layers.isolate', { label: item.label })}
              aria-pressed={isIsolated}
              title={isIsolated ? t('web.layers.showAll') : t('web.layers.isolateShort', { label: item.label })}
              onClick={() => onToggle(item.key)}
            >
              <span className="map-layer-dot" style={{ backgroundColor: item.color, color: item.color }} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export const GlobalSearchPalette = memo(function GlobalSearchPalette({
  isOpen,
  searchTerm,
  onSearchTermChange,
  items,
  totalCount,
  activeIndex,
  onActiveIndexChange,
  onClose,
  onSelect,
  outOfPeriodItems = [],
  previousPeriod = null,
  onRestorePreviousPeriod = null,
}) {
  const inputRef = useRef(null);
  const selectableItems = [...(items || []), ...(outOfPeriodItems || [])];

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (selectableItems.length === 0) return;
      onActiveIndexChange(current => Math.min(selectableItems.length - 1, current + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (selectableItems.length === 0) return;
      onActiveIndexChange(current => Math.max(0, current - 1));
      return;
    }

    if (event.key === 'Enter' && selectableItems[activeIndex]) {
      event.preventDefault();
      onSelect(selectableItems[activeIndex]);
    }
  };

  if (!isOpen) return null;

  const hasLocal = items.length > 0;
  const hasOutOfPeriod = outOfPeriodItems.length > 0;
  const showEmpty = totalCount === 0 && !hasOutOfPeriod && !searchTerm;
  const showNoMatch = !hasLocal && !hasOutOfPeriod && Boolean(searchTerm);

  const renderResultButton = (item, index) => (
    <button
      key={item.id}
      type="button"
      className={`nedi-row-btn global-search-item ${index === activeIndex ? 'is-active' : ''}`}
      role="option"
      aria-selected={index === activeIndex}
      data-nedi-anatomy="list-row"
      onMouseEnter={() => onActiveIndexChange(index)}
      onClick={() => onSelect(item)}
    >
      <span className="nedi-row-btn__copy global-search-item-main">
        <strong className="nedi-row-btn__title">{item.title}</strong>
        <small className="nedi-row-btn__subtitle">{item.subtitle || item.sourceKey}</small>
      </span>
      <span className="global-search-item-meta">
        {item.dateLabel || item.date || item.type}
      </span>
    </button>
  );

  return (
    <div className="global-search-overlay" onClick={onClose}>
      <section
        className="global-search-palette glass-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('web.search.global.title')}
        onClick={event => event.stopPropagation()}
      >
        <div className="global-search-header">
          <div>
            <h2>{t('web.search.global.title')}</h2>
            <p>{t('web.search.global.subtitle')}</p>
          </div>
          <AvButton type="button" variant="icon" className="global-search-close" onClick={onClose}>
            <span className="av-sr-only">{t('web.search.close')}</span>
            <span aria-hidden="true">×</span>
          </AvButton>
        </div>

        <input
          ref={inputRef}
          type="search"
          className="global-search-input"
          aria-label={t('web.search.input.a11y')}
          placeholder={t('web.search.input.placeholder')}
          value={searchTerm}
          onChange={event => onSearchTermChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />

        <div className="global-search-count" aria-live="polite">
          {totalCount === 0 && !hasOutOfPeriod
            ? (searchTerm ? t('web.search.noResultsPeriod') : t('web.search.noItemsLoaded'))
            : (totalCount === 1
              ? t('web.search.resultsCount', { count: totalCount })
              : t('web.search.resultsCountPlural', { count: totalCount }))}
        </div>

        {previousPeriod && onRestorePreviousPeriod ? (
          <AvButton
            type="button"
            variant="secondary"
            className="global-search-restore-period"
            onClick={onRestorePreviousPeriod}
            block
          >
            {t('web.search.restorePeriod', { start: previousPeriod.startDate, end: previousPeriod.endDate })}
          </AvButton>
        ) : null}

        {showEmpty ? (
          <p className="global-search-empty">{t('web.search.emptyLoad')}</p>
        ) : showNoMatch ? (
          <p className="global-search-empty">{t('web.search.emptyNoMatch')}</p>
        ) : (
          <div className="global-search-results" role="listbox" aria-label={t('web.search.results.a11y')}>
            {hasLocal ? items.map((item, index) => renderResultButton(item, index)) : null}
            {hasOutOfPeriod ? (
              <>
                <div className="global-search-section-label" role="presentation">
                  {t('web.search.otherPeriods')}
                </div>
                {outOfPeriodItems.map((item, index) =>
                  renderResultButton(item, items.length + index)
                )}
              </>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
});
